import React, { useState } from 'react';
import { Lightbulb, Tag, Folder, Plus, Pencil, Trash2, ChevronLeft, Search, X } from 'lucide-react';
import { createCategory, updateCategory, deleteCategory, createProject, updateProject, deleteProject } from '../lib/api';
import { useT } from '../lib/LanguageContext';

const withCount = (label, n) => (n ? `${label} (${n})` : label);

const Item = ({ icon: Icon, label, active, onClick, testid, color, onEdit, onDelete, editLabel, deleteLabel }) => (
  <div
    className={`group w-full flex items-center gap-4 pl-5 pr-3 py-2 text-[14px] rounded-r-full transition-colors cursor-pointer ${
      active ? 'bg-keep-accentBgSoft text-keep-text font-medium' : 'text-keep-text hover:bg-keep-hover'
    }`}
    onClick={onClick}
    data-testid={testid}
  >
    {Icon ? <Icon className="w-4 h-4 text-keep-textMuted flex-shrink-0 mt-0.5" /> : (
      <span className="w-3.5 h-3.5 rounded-full flex-shrink-0 mt-0.5" style={{ background: color || '#34A853' }} />
    )}
    <span className="flex-1 break-words">{label}</span>
    {onEdit && onDelete && (
      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 flex-shrink-0 self-start mt-0.5">
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-1 hover:bg-keep-surface rounded" aria-label={editLabel} data-testid="cat-edit"><Pencil className="w-3 h-3 text-keep-textMuted" /></button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 hover:bg-keep-dangerBg rounded" aria-label={deleteLabel} data-testid="cat-delete"><Trash2 className="w-3 h-3 text-keep-danger" /></button>
      </div>
    )}
  </div>
);

const EditItem = ({ icon: Icon, color, value, onChange, onSubmit, onCancel, testid }) => (
  <div className="w-full flex items-center gap-4 pl-5 pr-3 py-2 text-[14px]">
    {Icon ? <Icon className="w-4 h-4 text-keep-textMuted flex-shrink-0" /> : (
      <span className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ background: color || '#34A853' }} />
    )}
    <input
      value={value}
      autoFocus
      onFocus={(e) => e.target.select()}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSubmit();
        if (e.key === 'Escape') onCancel();
      }}
      className="flex-1 min-w-0 bg-transparent outline-none text-[13px] border-b border-keep-accent"
      data-testid={testid}
    />
  </div>
);

export default function Sidebar({
  open,
  onClose,
  currentFilter,
  onFilterChange,
  sourceCounts,
  categories,
  onCategoriesChanged,
  projects,
  onProjectsChanged,
}) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [addingProject, setAddingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [catSearchOpen, setCatSearchOpen] = useState(false);
  const [catSearchQuery, setCatSearchQuery] = useState('');
  const [projSearchOpen, setProjSearchOpen] = useState(false);
  const [projSearchQuery, setProjSearchQuery] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [editingProjId, setEditingProjId] = useState(null);
  const [editingProjName, setEditingProjName] = useState('');

  const closeCatSearch = () => {
    setCatSearchOpen(false);
    setCatSearchQuery('');
  };
  const closeProjSearch = () => {
    setProjSearchOpen(false);
    setProjSearchQuery('');
  };

  const filteredCategories = (categories || []).filter((c) =>
    (c.name || '').toLowerCase().includes(catSearchQuery.trim().toLowerCase()),
  );
  const filteredProjects = (projects || []).filter((p) =>
    (p.name || '').toLowerCase().includes(projSearchQuery.trim().toLowerCase()),
  );

  const submitNew = async () => {
    const n = newName.trim();
    if (!n) { setAdding(false); return; }
    try {
      await createCategory(n);
      setNewName('');
      setAdding(false);
      onCategoriesChanged?.();
    } catch (e) { alert(t('sidebar.createCategoryFailed')); }
  };

  const startEditCategory = (c) => {
    setEditingCatId(c.id);
    setEditingCatName(c.name);
  };

  const submitEditCategory = async (c) => {
    const name = editingCatName.trim();
    setEditingCatId(null);
    if (!name || name === c.name) return;
    try { await updateCategory(c.id, name, c.color); onCategoriesChanged?.(); } catch {}
  };

  const deleteCat = async (c) => {
    if (!window.confirm(t('sidebar.deleteCategoryConfirm', { name: c.name }))) return;
    try { await deleteCategory(c.id); onCategoriesChanged?.(); } catch {}
  };

  const submitNewProject = async () => {
    const n = newProjectName.trim();
    if (!n) { setAddingProject(false); return; }
    try {
      await createProject(n);
      setNewProjectName('');
      setAddingProject(false);
      onProjectsChanged?.();
    } catch (e) { alert(t('sidebar.createProjectFailed')); }
  };

  const startEditProject = (p) => {
    setEditingProjId(p.id);
    setEditingProjName(p.name);
  };

  const submitEditProject = async (p) => {
    const name = editingProjName.trim();
    setEditingProjId(null);
    if (!name || name === p.name) return;
    try { await updateProject(p.id, name, p.color); onProjectsChanged?.(); } catch {}
  };

  const deleteProj = async (p) => {
    if (!window.confirm(t('sidebar.deleteProjectConfirm', { name: p.name }))) return;
    try { await deleteProject(p.id); onProjectsChanged?.(); } catch {}
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 md:hidden" onClick={onClose} aria-hidden />
      )}
      <aside
        className={`fixed md:sticky left-0 top-14 md:top-16 z-40 md:z-0 bg-keep-surface md:bg-transparent h-[calc(100vh-3.5rem)] md:h-[calc(100vh-4rem)] border-r border-keep-topbarBorder md:border-r-0 shadow-keepHover md:shadow-none transition-all overflow-hidden ${
          open ? 'w-[280px] translate-x-0' : 'w-0 -translate-x-full md:translate-x-0 md:w-0'
        }`}
        data-testid="sidebar"
      >
        <nav className="py-2 pr-3 w-[280px] overflow-y-auto h-full keep-scroll">
          <Item icon={Lightbulb} label={t('sidebar.allNotes')} active={currentFilter === 'all'} onClick={() => onFilterChange('all')} testid="sidebar-filter-all" />
          <Item icon={Tag} label={withCount(t('sidebar.text'), sourceCounts?.text)} active={currentFilter === 'text'} onClick={() => onFilterChange('text')} testid="sidebar-filter-text" />
          <Item icon={Tag} label={withCount(t('sidebar.links'), sourceCounts?.url)} active={currentFilter === 'url'} onClick={() => onFilterChange('url')} testid="sidebar-filter-url" />
          <Item icon={Tag} label={withCount(t('sidebar.youtube'), sourceCounts?.youtube)} active={currentFilter === 'youtube'} onClick={() => onFilterChange('youtube')} testid="sidebar-filter-youtube" />
          <Item icon={Tag} label={withCount(t('sidebar.pdfs'), sourceCounts?.pdf)} active={currentFilter === 'pdf'} onClick={() => onFilterChange('pdf')} testid="sidebar-filter-pdf" />
          <Item icon={Tag} label={withCount(t('sidebar.images'), sourceCounts?.image)} active={currentFilter === 'image'} onClick={() => onFilterChange('image')} testid="sidebar-filter-image" />

          <div className="my-3 mx-5 border-t border-keep-border" />
          <div className="px-5 py-1 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-keep-textMuted">{t('sidebar.categories')}</span>
            <button
              onClick={() => (catSearchOpen ? closeCatSearch() : setCatSearchOpen(true))}
              className="p-1 rounded hover:bg-keep-hover"
              aria-label={catSearchOpen ? t('sidebar.closeSearch') : t('sidebar.searchCategories')}
              data-testid="sidebar-category-search-toggle"
            >
              {catSearchOpen ? (
                <X className="w-3.5 h-3.5 text-keep-textMuted" />
              ) : (
                <Search className="w-3.5 h-3.5 text-keep-textMuted" />
              )}
            </button>
          </div>

          {catSearchOpen && (
            <div className="px-5 pb-1.5">
              <input
                value={catSearchQuery}
                autoFocus
                onChange={(e) => setCatSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') closeCatSearch(); }}
                placeholder={t('sidebar.searchCategories')}
                className="w-full bg-transparent outline-none text-[13px] border-b border-keep-accent pb-0.5"
                data-testid="sidebar-category-search-input"
              />
            </div>
          )}

          {catSearchOpen && filteredCategories.length === 0 && (
            <div className="px-5 py-1.5 text-[12.5px] text-keep-textFaint italic">{t('sidebar.noMatches')}</div>
          )}

          {filteredCategories.map((c) =>
            editingCatId === c.id ? (
              <EditItem
                key={c.id}
                color={c.color}
                value={editingCatName}
                onChange={setEditingCatName}
                onSubmit={() => submitEditCategory(c)}
                onCancel={() => setEditingCatId(null)}
                testid={`sidebar-category-edit-input-${c.id}`}
              />
            ) : (
              <Item
                key={c.id}
                color={c.color}
                label={c.name}
                active={currentFilter === `cat:${c.id}`}
                onClick={() => onFilterChange(`cat:${c.id}`)}
                testid={`sidebar-category-${c.id}`}
                onEdit={() => startEditCategory(c)}
                onDelete={() => deleteCat(c)}
                editLabel={t('common.edit')}
                deleteLabel={t('common.delete')}
              />
            ),
          )}

          {adding ? (
            <div className="px-5 py-1.5 flex items-center gap-2">
              <input
                value={newName}
                autoFocus
                onChange={(e) => setNewName(e.target.value)}
                onBlur={submitNew}
                onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); if (e.key === 'Escape') { setAdding(false); setNewName(''); }}}
                placeholder={t('sidebar.categoryName')}
                className="flex-1 bg-transparent outline-none text-[13px] border-b border-keep-accent"
                data-testid="sidebar-new-category-input"
              />
            </div>
          ) : (
            <button onClick={() => setAdding(true)} className="w-full text-left pl-5 pr-3 py-2 text-[13px] text-keep-textMuted hover:bg-keep-hover rounded-r-full flex items-center gap-3" data-testid="sidebar-add-category">
              <Plus className="w-4 h-4" />
              {t('sidebar.createCategory')}
            </button>
          )}

          <div className="my-3 mx-5 border-t border-keep-border" />
          <div className="px-5 py-1 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-keep-textMuted">{t('sidebar.projects')}</span>
            <button
              onClick={() => (projSearchOpen ? closeProjSearch() : setProjSearchOpen(true))}
              className="p-1 rounded hover:bg-keep-hover"
              aria-label={projSearchOpen ? t('sidebar.closeSearch') : t('sidebar.searchProjects')}
              data-testid="sidebar-project-search-toggle"
            >
              {projSearchOpen ? (
                <X className="w-3.5 h-3.5 text-keep-textMuted" />
              ) : (
                <Search className="w-3.5 h-3.5 text-keep-textMuted" />
              )}
            </button>
          </div>

          {projSearchOpen && (
            <div className="px-5 pb-1.5">
              <input
                value={projSearchQuery}
                autoFocus
                onChange={(e) => setProjSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') closeProjSearch(); }}
                placeholder={t('sidebar.searchProjects')}
                className="w-full bg-transparent outline-none text-[13px] border-b border-keep-accent pb-0.5"
                data-testid="sidebar-project-search-input"
              />
            </div>
          )}

          {projSearchOpen && filteredProjects.length === 0 && (
            <div className="px-5 py-1.5 text-[12.5px] text-keep-textFaint italic">{t('sidebar.noMatches')}</div>
          )}

          {filteredProjects.map((p) =>
            editingProjId === p.id ? (
              <EditItem
                key={p.id}
                icon={Folder}
                value={editingProjName}
                onChange={setEditingProjName}
                onSubmit={() => submitEditProject(p)}
                onCancel={() => setEditingProjId(null)}
                testid={`sidebar-project-edit-input-${p.id}`}
              />
            ) : (
              <Item
                key={p.id}
                icon={Folder}
                label={p.name}
                active={currentFilter === `proj:${p.id}`}
                onClick={() => onFilterChange(`proj:${p.id}`)}
                testid={`sidebar-project-${p.id}`}
                onEdit={() => startEditProject(p)}
                onDelete={() => deleteProj(p)}
                editLabel={t('common.edit')}
                deleteLabel={t('common.delete')}
              />
            ),
          )}

          {addingProject ? (
            <div className="px-5 py-1.5 flex items-center gap-2">
              <input
                value={newProjectName}
                autoFocus
                onChange={(e) => setNewProjectName(e.target.value)}
                onBlur={submitNewProject}
                onKeyDown={(e) => { if (e.key === 'Enter') submitNewProject(); if (e.key === 'Escape') { setAddingProject(false); setNewProjectName(''); }}}
                placeholder={t('sidebar.projectName')}
                className="flex-1 bg-transparent outline-none text-[13px] border-b border-keep-accent"
                data-testid="sidebar-new-project-input"
              />
            </div>
          ) : (
            <button onClick={() => setAddingProject(true)} className="w-full text-left pl-5 pr-3 py-2 text-[13px] text-keep-textMuted hover:bg-keep-hover rounded-r-full flex items-center gap-3" data-testid="sidebar-add-project">
              <Plus className="w-4 h-4" />
              {t('sidebar.createProject')}
            </button>
          )}
        </nav>
      </aside>
    </>
  );
}
